import {useEffect,useRef,useState} from 'react';

type Line={line_number:number;description:string;quantity:number;unit_amount_minor:number;amount_minor:number};
export type CustomerInvoice={invoice_id:string;invoice_number:string;company_name:string;state:string;total_minor:number;paid_minor:number;balance_minor:number;currency:'USD';due_date:string|null;issued_at:string;lines:Line[]};

const labels:Record<string,string>={issued:'Awaiting payment',checkout_open:'Payment in progress',partially_paid:'Partially paid',settled:'Paid',external_payment_confirmed:'Paid',waived:'Waived — nothing due',void:'Cancelled — nothing due',manual_resolution_required:'Under review by the servicing company'};
const uuid=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const amount=(v:unknown)=>typeof v==='number'&&Number.isSafeInteger(v)&&v>=0;
const money=(minor:number)=>new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(minor/100);

function readable(value:unknown):value is CustomerInvoice{
  if(!value||typeof value!=='object'||Array.isArray(value))return false;
  const v=value as Record<string,unknown>;
  return typeof v.invoice_id==='string'&&uuid.test(v.invoice_id)&&typeof v.invoice_number==='string'&&v.invoice_number.length>0&&typeof v.company_name==='string'
    &&typeof v.state==='string'&&Object.hasOwn(labels,v.state)&&amount(v.total_minor)&&amount(v.paid_minor)&&amount(v.balance_minor)&&v.currency==='USD'
    &&(v.due_date===null||typeof v.due_date==='string')&&typeof v.issued_at==='string'&&Array.isArray(v.lines)
    &&v.lines.every(l=>!!l&&typeof l==='object'&&typeof (l as Line).description==='string'&&amount((l as Line).quantity)&&amount((l as Line).unit_amount_minor)&&amount((l as Line).amount_minor));
}

async function loadCustomerInvoices():Promise<CustomerInvoice[]>{
  const response=await fetch('/api/v1/customer/invoices',{credentials:'same-origin',headers:{Accept:'application/json'}});
  if(!response.ok)throw new Error('Invoices unavailable.');
  const body=await response.json() as {invoices?:unknown};
  if(!Array.isArray(body.invoices)||!body.invoices.every(readable))throw new Error('Invoice response mismatch.');
  return body.invoices;
}

/** Read-only invoices billed to the signed-in customer's own orders. Payment is arranged with the servicing company. */
export function CustomerInvoices({refreshKey=0}:{refreshKey?:number}){
  const[items,setItems]=useState<CustomerInvoice[]|null>(null),[failed,setFailed]=useState(false),[reload,setReload]=useState(0);
  const resultRef=useRef<HTMLDivElement>(null),focusNext=useRef(false);
  useEffect(()=>{
    let active=true;const focus=focusNext.current;focusNext.current=false;
    let focusFrame:number|undefined;
    const finish=(next:CustomerInvoice[]|null)=>{
      if(!active)return;
      active=false;clearTimeout(timer);
      if(next===null)setFailed(true);else setItems(next);
      if(focus)focusFrame=requestAnimationFrame(()=>resultRef.current?.focus());
    };
    // Observe the GET and body parsing, without accepting a late result or retrying automatically.
    const timer=setTimeout(()=>finish(null),15000);
    setItems(null);setFailed(false);
    void loadCustomerInvoices().then(next=>finish(next),()=>finish(null));
    return()=>{active=false;clearTimeout(timer);if(focusFrame!==undefined)cancelAnimationFrame(focusFrame);};
  },[refreshKey,reload]);
  return <section className="panel" aria-labelledby="customer-invoices-title">
    <h2 id="customer-invoices-title">Invoices</h2>
    <div ref={resultRef} tabIndex={-1}>
      {failed&&<><p role="status">Invoices could not be loaded. This does not mean nothing is owed.</p><button type="button" className="secondary" onClick={()=>{focusNext.current=true;setReload(n=>n+1);}}>Try loading invoices again</button></>}
      {!failed&&items===null&&<p role="status">Checking for invoices…</p>}
      {items&&items.length===0&&<p>No invoices have been issued for your orders.</p>}
      {items&&items.length>0&&<>
        <p>Pay the servicing company directly using the instructions they provide. Online payment is not available in ProFenz yet.</p>
        <ul className="customer-invoice-list">{items.map(i=><li key={i.invoice_id}>
          <h3>{i.invoice_number} · {i.company_name}</h3>
          <p>{labels[i.state]} · Total {money(i.total_minor)} · Paid {money(i.paid_minor)} · Balance due {money(i.balance_minor)}{i.due_date?` · Due ${i.due_date}`:''}</p>
          <table><caption>Charges on {i.invoice_number}</caption><thead><tr><th scope="col">Description</th><th scope="col">Quantity</th><th scope="col">Unit price</th><th scope="col">Amount</th></tr></thead>
            <tbody>{i.lines.map(l=><tr key={l.line_number}><td>{l.description}</td><td>{l.quantity}</td><td>{money(l.unit_amount_minor)}</td><td>{money(l.amount_minor)}</td></tr>)}</tbody></table>
        </li>)}</ul>
      </>}
    </div>
  </section>;
}
